import { ColumnValueChangedEvent } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { HeaderValueGetterParams } from 'ag-grid-enterprise';
import { StockExtended } from 'apis/StoreFunctionsApi';
import { Title } from "components";
import { AgGrid, AgGridRef, ColumnType, GridState, ZColDef } from "components/AgGrid/AgGrid";
import { AgGridToolbar, AgGridToolbarRef } from 'components/AgGrid/AgGridToolbar';
import { AnyFilter, createBooleanFilter, createBooleanSelectFilter, createMultiSelectFilter, createSingleSelectFilter, FilterState } from "components/AgGrid/FilterComponents/Filters";
import { SearchState } from 'components/AgGrid/FilterComponents/Search';
import { PaginationState } from 'components/AgGrid/Pagination';
import { ZButton } from 'components/ZButton';
import { ZIcon } from 'components/ZIcon';
import { ZLabel } from 'components/ZLabel';
import { ZMultiSelectDropdown } from 'components/ZMultiSelectDropdown';
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import { ScheduleCodes } from 'constants/constants';
import { capitalizeFirstLetter } from 'helpers/Functions';
import { ZIconType } from 'helpers/utility/iconMap';
import { Z } from 'models/common/components';
import { ManagedDepartment } from 'models/dbModels/managedDepartments/ManagedDepartment';
import { ManagedStock } from 'models/dbModels/managedStock/ManagedStock';
import { ManagedStockGroup } from 'models/dbModels/managedStockGroups/ManagedStockGroup';
import { ManagedSubdepartment } from 'models/dbModels/managedSubdepartments/ManagedSubdepartment';
import { ManagedSupplier } from 'models/dbModels/managedSuppliers/ManagedSupplier';
import { StoreExtended } from 'models/dbModels/stores/Store';
import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { getDepartments } from 'redux/slices/Departments';
import { getStockGroups } from 'redux/slices/StockGroups';
import { cleanStockDetails, getStockTable } from "redux/slices/Stocks";
import { cleanStoreStocksDetails, storeStockSearch } from "redux/slices/StoreStocks";
import { getSubDepartments } from 'redux/slices/SubDepartment';
import { getSuppliers } from 'redux/slices/Suppliers';
import { RootState } from "redux/store";


interface ExtendedCol<T> {
	columnType?: 'string' | 'price',
	field: keyof T,
	renderer?: (row: T) => JSX.Element

}
interface RowData extends ManagedStock {
	test: string;
	test2: number;
	markup: number;
}

export interface TestInterface<T> {
	GetApiInterface(): string[];
	IsColumnPinned(colId: keyof T): boolean;
	ColumnType(colId: keyof T): ColumnType;
	GetValueForCell(row: T, colId: keyof T): unknown;
	OverrideDeleteModal?(): void;
}

class TestReportGrid implements TestInterface<RowData> {
	GetApiInterface(): string[] {
		throw new Error("Method not implemented.");
	}

	ColumnType(colId: keyof RowData) {
		switch (colId) {
			case "test2":
				return ColumnType.Price;
		}

		return ColumnType.String;
	}

	IsColumnPinned(colId: keyof RowData): boolean {
		switch (colId) {
			case "test":
				return true;
			case "test2":
				return false;
		}

		return false;
	}

	GetColumnHeaderName(colId: keyof RowData): string {
		switch (colId) {
			case "markup":
				return "Markup";
			case "test":
				return "Name";
		}

		return "Unknown Header";
	}

	GetFormattedValueForCell() {

	}

	GetValueForCell(row: RowData, colId: keyof RowData) {
		if (colId === "markup") {
			return row.baseSellPrice * 1.2;
		}
		else {
			return [row[colId],];
		}
	}
}

export function GroupStockReport() {
	const grid = new TestReportGrid(); // Can pass this into AgGrid and use the methods within the interface for various things

	const report = useAppSelector((state: RootState) => state.reports);
	const stocks = useAppSelector((state: RootState) => state.stocks);
	const stores = useAppSelector((state: RootState) => state.stores);
	const storeStocks = useAppSelector((state: RootState) => state.storeStocks);
	const dispatch = useAppDispatch();
	const departments = useAppSelector((state: RootState) => state.department);
	const subDepartments = useAppSelector((state: RootState) => state.subDepartment);
	const suppliers = useAppSelector((state: RootState) => state.suppliers);
	const stockGroups = useAppSelector((state: RootState) => state.stockGroups);

	const toolbarRef = useRef<AgGridToolbarRef>(null);
	const gridRef = useRef<AgGridRef>(null);
	const gridSearch = useRef<{searchState?: SearchState, filterState?: FilterState}>({});
	const [selectedDepartment, setSelectedDepartment] = useState<ManagedDepartment | undefined>(undefined);

	const [loading, setLoading] = useState(true);

	const [storeSelection, setStoreSelection] = useState<StoreExtended[] | undefined>();

	useEffect(() => {
		dispatch(getDepartments());
		dispatch(getSubDepartments());
		dispatch(getSuppliers());
		dispatch(getStockGroups());
	}, []);

	useEffect(() => {
		setStoreSelection(stores.table.filter(s => s.connected));
	}, [stores.table]);

	const SOH_DANGER_THRESHOLD = -3;
	const SOH_WARNING_THRESHOLD = 0;
	const COST_EX_DANGER_THRESHOLD = -3;
	const COST_EX_WARNING_THRESHOLD = 0;
	const STOCK_VALUE_DANGER_THRESHOLD = -2;
	const STOCK_VALUE_WARNING_THRESHOLD = 0;

	const convertAggFuncHeader = (params: HeaderValueGetterParams<any, any>) => {
		let headerName = params.colDef.headerName ?? "Unknown";
		if (params.location === "header") {
			const aggFunc = params.column?.getAggFunc();
			if (aggFunc) {
				headerName += ` (${capitalizeFirstLetter(aggFunc.toString())})`;
			}
		}
		return headerName;
	}

	const defaultNumberColumnOptions = {
		enableValue: true,
		suppressMovable: false,
		enableRowGroup: true,
		headerValueGetter: convertAggFuncHeader, 
		headerClass: "ag-right-aligned-header", 
		allowedAggFuncs: ['avg', 'sum', 'min', 'max'],
		cellRendererParams: { align: "right" },
		defaultAggFunc: 'sum', 
		initialAggFunc: 'sum',
	}

	const columnDefs: ZColDef<any>[] = useMemo(() => [
		{ field: "name", rowGroup: true, hide: true, },
		{
			field: "dept", //rowGroup: true, hide: true,
			headerName: "Department → Subdepartment",
			valueGetter: (params: any) => {
				// If it's a group row, just show the group's key, which will be the combined value
				if (!params.node.group) {
					return params.data && params.data.departmentName
						? params.data.departmentName + (params.data.subDepartmentName ? " → " + params.data.subDepartmentName : "")
						: null;
				}
				else {
					return null;
				}
			}
		},
		{
			headerName: 'Stock Name',
			pinned: true,
			lockPinned: true,
			lockPosition: true,
			suppressMovable: true,
			lockVisible: true,
			colId: 'stockGroup',
			minWidth: 220,
			showRowGroup: 'name',
			cellRenderer: 'agGroupCellRenderer',
			valueGetter: (params: any) => {
				return params.data ? params.data.storeName : null;
			},
			cellRendererParams: {
				// turn off the row count
				suppressCount: true,
			}
		},
		{ 
			field: "soh", 
			headerName: "SOH",
			...defaultNumberColumnOptions,
			// cellRendererSelector: (params) => {
			// 	if (params.value < SOH_DANGER_THRESHOLD) {
			// 		return {
			// 			component: 'integerCellRenderer',
			// 			params: {
			// 				align: "right",
			// 				decorators: [
			// 					{ decorator: <ZIcon icon={ZIconType.Alert} variant='danger' />, position: "start", containerClassName: "me-auto"}
			// 				]
			// 			}
			// 		}
			// 	} else if (params.value < SOH_WARNING_THRESHOLD) {
			// 		return {
			// 			component: 'integerCellRenderer',
			// 			params: {
			// 				align: "right",
			// 				decorators: [
			// 					{ decorator: <ZIcon icon={ZIconType.Alert} variant='warning' />, position: "start", containerClassName: "me-auto"}
			// 				]
			// 			}
			// 		}
			// 	} else {
			// 		return {
			// 			component: 'integerCellRenderer',
			// 			params: { align: "right" }
			// 		}
			// 	}
			// },
			// cellStyle: (params: any) => {
			// 	const color = params.value < SOH_DANGER_THRESHOLD ? 'var(--bs-danger)' : params.value < SOH_WARNING_THRESHOLD ? 'var(--bs-warning)' : '';
			// 	return { color: color };
			// },
		},
		{ 
			field: "costEx",
			headerName: "Cost Ex",
			cellRenderer: 'priceCellRenderer', 
    		filter: true,
			...defaultNumberColumnOptions,
			defaultAggFunc: 'avg',
			initialAggFunc: 'avg',
			// cellRendererSelector: (params) => {
			// 	if (params.value < COST_EX_DANGER_THRESHOLD) {
			// 		return {
			// 			component: 'priceCellRenderer',
			// 			params: {
			// 				align: "right",
			// 				decorators: [
			// 					{ decorator: <ZIcon icon={ZIconType.Alert} variant='danger' />, position: "start", containerClassName: "me-auto"}
			// 				]
			// 			}
			// 		}
			// 	} else if (params.value < COST_EX_WARNING_THRESHOLD) {
			// 		return {
			// 			component: 'priceCellRenderer',
			// 			params: {
			// 				align: "right",
			// 				decorators: [
			// 					{ decorator: <ZIcon icon={ZIconType.Alert} variant='warning' />, position: "start", containerClassName: "me-auto"}
			// 				]
			// 			}
			// 		}
			// 	} else {
			// 		return {
			// 			component: 'priceCellRenderer',
			// 			params: { align: "right" }
			// 		}
			// 	}
			// },
			// cellStyle: (params: any) => {
			// 	const color = params.value < COST_EX_DANGER_THRESHOLD ? 'var(--bs-danger)' : params.value < COST_EX_WARNING_THRESHOLD ? 'var(--bs-warning)' : '';
			// 	return { color: color };
			// },
		},
		{
			field: "stockValueTotal",
			headerName: "Stock Value",
			cellRenderer: 'priceCellRenderer',
			...defaultNumberColumnOptions,
			valueGetter: (params: any) => {
				return params.data ? params.data.soh * params.data.costEx : null;
			}
			// cellRendererSelector: (params) => {
			// 	if (params.value < STOCK_VALUE_WARNING_THRESHOLD) {
			// 		return {
			// 			component: 'priceCellRenderer',
			// 			params: {
			// 				align: "right",
			// 				decorators: [
			// 					{ decorator: <ZIcon icon={ZIconType.Alert} variant='danger' />, position: "start", containerClassName: "me-auto"}
			// 				]
			// 			}
			// 		}
			// 	} else if (params.value < STOCK_VALUE_WARNING_THRESHOLD) {
			// 		return {
			// 			component: 'priceCellRenderer',
			// 			params: {
			// 				align: "right",
			// 				decorators: [
			// 					{ decorator: <ZIcon icon={ZIconType.Alert} variant='warning' />, position: "start", containerClassName: "me-auto"}
			// 				]
			// 			}
			// 		}
			// 	} else {
			// 		return {
			// 			component: 'priceCellRenderer',
			// 			params: { align: "right" }
			// 		}
			// 	}
			// },
			// cellStyle: (params: any) => {
			// 	const color = params.value < STOCK_VALUE_DANGER_THRESHOLD ? 'var(--bs-danger)' : params.value < STOCK_VALUE_WARNING_THRESHOLD ? 'var(--bs-warning)' : '';
			// 	return { color: color };
			// },
		},
		{
			field: "rrp",
			headerName: "RRP",
			cellRenderer: 'priceCellRenderer',
			...defaultNumberColumnOptions,
			defaultAggFunc: 'avg',
			initialAggFunc: 'avg',
		},
	], []);

	const [paginationState, setPaginationState] = useState<PaginationState>();

	const [gridData, setGridData] = useState<(StockExtended & {storeName: string})[]>([]);

	function handleSubmit ({paginationState, searchState, filterState}: {paginationState?: PaginationState, searchState?: SearchState, filterState?: FilterState}) {
		if (storeSelection == null || storeSelection.length === 0) {
			dispatch(cleanStoreStocksDetails());
			dispatch(cleanStockDetails());
			setGridData([]);
			return;
		}
		setLoading(true);
		dispatch(getStockTable({
			offset: paginationState?.offset ?? 0,
			limit: paginationState?.limit ?? 100,
			keyword: searchState?.keyword,
			contains: searchState?.contains,
			...filterState
		})).then((res) => {
            const ids = res.data.map((x) => {
                return x.stockId;
            });

			if (ids.length > 0) {
				const stores = storeSelection?.map(s => s.id) ?? [-1];
				dispatch(storeStockSearch({ ids: ids, stores: stores })).then((res2) => {
					const storeStockData = res2.flatMap(i => i.response?.data.map(x => ({...x, storeName: i.storeName})) ?? []) || [];
					
					// For each store, check if stock exists, if not, we add it with the stock id, stock name and store name
					// If it does exist, do nothing
					res2.forEach(storeData => {
						const missingStocks = res.data.filter(stock => !storeData.response?.data.some(x => x.id === stock.stockId));
						storeStockData.push(...missingStocks.map(stock => {
							const row: Partial<StockExtended> = {};
							return {
								...row,
								id: stock.stockId, 
								storeName: storeData.storeName, 
								name: stock.name
							} as StockExtended & { storeName: string };
						}));
					})
					setGridData(storeStockData.sort(((a, b) => a.name.localeCompare(b.name))));
					setLoading(false);
				});
			 } else {
				dispatch(cleanStoreStocksDetails()); setGridData([]); setLoading(false);
			 }
		});
	}

	useEffect(() => {
		console.log("Grid data: ", gridData);
	}, [gridData]);


	function handlePaginate(gridState: GridState) {
		setPaginationState(gridState.paginationState);
		handleSubmit({
			paginationState: gridState.paginationState,
			searchState: gridSearch.current.searchState, 
			filterState: gridSearch.current.filterState
		});
	}

	return (
		<>
			<Title pageTitle={"Stock Report"} crumbs={[{ path: "/app/reports/test" }]} />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
								<AgGridToolbar 
									search={true}
									searchProps={
										{totalRows: stocks.totalRows, placeholder: "by Stock Name"}
									}
									ref={toolbarRef}
									className="my-2 px-2 align-items-end"
									handleSubmit={(s,f) => {
										gridSearch.current = { searchState: s, filterState: f };
										handleSubmit({paginationState: paginationState, searchState: s, filterState: f});
									}}
									filters={[
										createSingleSelectFilter<ManagedSupplier>({
											label: "Supplier",
											filterName: "supplierIds",
											options: suppliers.table,
											value: (option) => [option.id],
											display: (option) => option.name,
											hasNoneOption: true,
										}),
										createSingleSelectFilter<ManagedDepartment>({
											label: "Department",
											filterName: "departmentIds",
											options: departments.table,
											value: (option) => [option.id],
											display: (option) => option.name,
											onFilterChange: (e) => setSelectedDepartment(e),
											hasNoneOption: true,
										}),
										createSingleSelectFilter<ManagedSubdepartment>({
											label: "Sub Department",
											filterName: "subdepartmentIds",
											options: subDepartments.table.filter( i=> i.departmentId === selectedDepartment?.id),
											value: (option) => [option.id],
											display: (option) => option.name,
											hasNoneOption: true,
										}),
										createSingleSelectFilter<ManagedStockGroup>({
											label: "Stock Group",
											filterName: "stockGroupIds",
											options: stockGroups.table,
											value: (option) => [option.id],
											display: (option) => option.name,
											hasNoneOption: true,
										}),
										createBooleanSelectFilter({
											label: "Active",
											filterName: "active",
											hasNoneOption: true,
										}),
										createBooleanSelectFilter({
											label: "Ethical",
											filterName: "ethical",
											hasNoneOption: true,
										}),
										createSingleSelectFilter<string>({
											label: "Schedule",
											filterName: "schedule",
											options: ScheduleCodes,
											value: (option) => option,
											display: (option) => option,
											hasNoneOption: true,
										}),
										createBooleanSelectFilter({
											label: "PBS",
											filterName: "pbs",
											hasNoneOption: true,
										}),
									]}
									children={(<div className="d-inline-flex gap-1 w-75">
										<ZMultiSelectDropdown<StoreExtended>
											onDropdownClose={() => 
												handleSubmit({paginationState: paginationState, searchState: gridSearch.current.searchState, filterState: gridSearch.current.filterState})}
											containerClassName='w-75 ms-auto'
											label="Stores:"
											placeholder='Select stores'
											values={storeSelection}
											options={stores.table}
											display={(s) => s.name}
											value={(s) => s.id}
											minMenuWidthPx={300}
											disabled={(s) => !s?.connected ?? false}
											onSelectionChange={(opt) => setStoreSelection(opt)}
											hasAllOption
											hasSearch
											searchPlaceholder='Search stores'
										/>
									</div>
								)}
							/>
							<Row className="mb-1">
								<Col xs={12} className='h-fit'>
									<AgGrid
										heightPx={500}
										gridInterface={grid}
										rowData={gridData}
										getRowId={(row) => row.data.id + "|" + row.data.storeName}
										columnDefs={columnDefs}
										onCellClicked={() => console.log("Clicked!")}
										onCellEditingStopped={(rowId, colId, newValue) => {
											console.log("rowId: " + rowId);
											console.log("colId: " + colId);
											console.log("New value: " + newValue);
										}}
										sideBar={{
											toolPanels: [
												{
													id: 'columns',
													labelDefault: 'Columns',
													labelKey: 'columns',
													iconKey: 'columns',
													toolPanel: 'agColumnsToolPanel',
													toolPanelParams: {
														suppressPivots: true,
														suppressPivotMode: true,
														suppressRowGroups: true,
													}
												},
											]
										}}
										pagination={
											{ type: "serverside", totalRows: stocks.totalRows }
										}
										handleSubmit={handlePaginate}
										loading={loading}
									/>
								</Col>
							</Row>
							<Row className='mt-2'>
								<Col sm={6}>
									<Link to="/app/reports" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
										<i className="mdi mdi-arrow-left"></i> Back to Reports {' '}
									</Link>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}